import React, { useState } from 'react';
import { api } from '../services/api';
import {
  Mail,
  Send,
  MessageSquare,
  Building,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Gamepad2,
  Sparkles,
} from 'lucide-react';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    setLoading(true);
    setStatus({ type: null, message: '' });

    try {
      await api.submitContact(formData);
      setStatus({
        type: 'success',
        message: 'Your message has been sent to GSR STUDIO! Our team will get back to you shortly.',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      setStatus({
        type: 'error',
        message: err.message || 'Failed to send message. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16 pb-24">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-950/60 border border-cyan-500/30 text-cyan-300 text-xs font-semibold">
          <Mail className="w-3.5 h-3.5 text-cyan-400" />
          <span>Get in Touch</span>
        </div>
        <h1 className="text-4xl font-black text-white tracking-tight">Contact GSR STUDIO</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          Have an inquiry, publishing proposal, feedback on our games, or wanting to join our team? Drop us a transmission.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {/* Contact Information / Cards */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-slate-900/40 rounded-3xl border border-slate-800 p-8 space-y-6">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              <span>Direct Channels</span>
            </h3>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-slate-950/50 border border-slate-800/80">
                <div className="p-2.5 rounded-xl bg-cyan-500/10 text-cyan-400">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white">General & Press</h4>
                  <p className="text-xs text-slate-400 mt-0.5">contact@gsrstudio.games</p>
                  <p className="text-[11px] text-slate-500 mt-1">Average response within 24h</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-2xl bg-slate-950/50 border border-slate-800/80">
                <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400">
                  <Gamepad2 className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white">Publishing & Business</h4>
                  <p className="text-xs text-slate-400 mt-0.5">business@gsrstudio.games</p>
                  <p className="text-[11px] text-slate-500 mt-1">For partners, distributors & events</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-2xl bg-slate-950/50 border border-slate-800/80">
                <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400">
                  <Building className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white">Global Studio Core</h4>
                  <p className="text-xs text-slate-400 mt-0.5">Distributed Remote / Tokyo & Silicon Valley</p>
                  <p className="text-[11px] text-slate-500 mt-1">Cloud native creative collaboration</p>
                </div>
              </div>
            </div>
          </div>

          {/* Discord / Community Banner */}
          <div className="bg-gradient-to-br from-indigo-950/40 to-slate-900 rounded-3xl border border-indigo-500/30 p-8 space-y-4">
            <h4 className="text-lg font-bold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-400" />
              <span>Join Community Discord</span>
            </h4>
            <p className="text-slate-300 text-xs leading-relaxed">
              Join 50,000+ players in our community server for live playtesting keys, patch notes, and developer AMAs.
            </p>
            <a
              href="https://discord.gg"
              target="_blank"
              rel="noreferrer"
              className="inline-block px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition shadow-lg shadow-indigo-600/20"
            >
              discord.gg/gsrstudio
            </a>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-7 bg-slate-900/60 rounded-3xl border border-slate-800 p-8 sm:p-10">
          <h2 className="text-2xl font-bold text-white mb-2">Send a Message</h2>
          <p className="text-slate-400 text-xs mb-8">
            Fill out the form below. Messages are saved securely in our edge database and dispatched to our studio inbox.
          </p>

          {status.type && (
            <div
              className={`mb-6 p-4 rounded-2xl flex items-start gap-3 border text-xs leading-relaxed ${
                status.type === 'success'
                  ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300'
                  : 'bg-rose-950/60 border-rose-500/40 text-rose-300'
              }`}
            >
              {status.type === 'success' ? (
                <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-400" />
              ) : (
                <AlertCircle className="w-5 h-5 shrink-0 text-rose-400" />
              )}
              <span>{status.message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">
                  Your Name <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Alex Mercer"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">
                  Email Address <span className="text-rose-400">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="alex@domain.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">
                Subject
              </label>
              <input
                type="text"
                placeholder="Game feedback, job inquiry, press..."
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">
                Message <span className="text-rose-400">*</span>
              </label>
              <textarea
                rows={5}
                required
                placeholder="Write your transmission here..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 transition resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 transition disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Transmit Message</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
